"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { chromeLocaleForPath, type ChromeLocale } from "@/lib/chrome-locale";

// The dialog, its Modal primitive and the search directory load on first open;
// see GlobalCommandPaletteDialog. Nothing below may import the directory: this
// trigger ships in every page's first-load JS.
const GlobalCommandPaletteDialog = dynamic(() => import("./GlobalCommandPaletteDialog"), {
  ssr: false,
});

/** The trigger's visible label and accessible name, in the page's language. */
const TRIGGER_LABEL: Record<ChromeLocale, string> = { en: "Search", hi: "खोजें" };

export default function GlobalCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const router = useRouter();
  const pathname = usePathname();
  const locale = chromeLocaleForPath(pathname);
  const trigger = useRef<HTMLButtonElement>(null);

  // Escape is the dialog's (the Modal closes on it); this only owns the
  // Ctrl/Cmd+K shortcut.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // A client-side navigation keeps the layout mounted: without this, tapping
  // the sticky chat pill opened /hi/muft-jyotish-chat/ with the palette still
  // over it. Same pattern as SiteHeaderBar's menu.
  const renderedPathname = useRef(pathname);
  useEffect(() => {
    if (renderedPathname.current === pathname) return;
    renderedPathname.current = pathname;
    setIsOpen(false);
    setQuery("");
  }, [pathname]);

  const close = useCallback(() => {
    setIsOpen(false);
    // The Modal restores its opener, but the opener it captured can be the
    // search field once that takes focus; hand focus back to the trigger.
    window.requestAnimationFrame(() => trigger.current?.focus());
  }, []);

  const handleSelect = (href: string) => {
    setIsOpen(false);
    setQuery("");
    router.push(href);
  };

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={TRIGGER_LABEL[locale]}
        aria-haspopup="dialog"
        aria-keyshortcuts="Control+K Meta+K"
        className="inline-flex size-11 items-center justify-center gap-1.5 rounded-lg border border-border-default bg-surface-warm text-caption text-text-secondary transition hover:border-border-interactive hover:text-text-primary sm:w-auto sm:px-3"
      >
        <SearchIcon />
        <span className="hidden font-medium sm:inline">{TRIGGER_LABEL[locale]}</span>
      </button>

      {isOpen && (
        <GlobalCommandPaletteDialog
          query={query}
          locale={locale}
          onQueryChange={setQuery}
          onClose={close}
          onSelect={handleSelect}
        />
      )}
    </>
  );
}

/** Inline, not the icon registry: this trigger is in every page's first load. */
function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
